import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Instagram, Phone, Mail, MessageCircle, Sun, Moon, Linkedin, ChevronLeft, ChevronRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import emailjs from '@emailjs/browser';

const Home = () => {
  const { toast } = useToast();
  const [isDark, setIsDark] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [modalIndex, setModalIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [showHeader, setShowHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Success stories data with images (in order: a, c, d, b, e)
  const successStories = [
    { 
      id: 1, 
      company: 'KPMG', 
      role: 'Manager - Financial Services Operations',
      description: 'Successfully secured interview for senior management position',
      image: require('../images/a.jpg')
    },
    { 
      id: 3, 
      company: 'Disney', 
      role: 'Technical Project Lead',
      description: 'Scheduled virtual interview for leadership role',
      image: require('../images/c.jpg')
    },
    { 
      id: 2, 
      company: 'British Airways', 
      role: 'Creative Tech & Operations Manager',
      description: 'Application reviewed and progressed to interview stage',
      image: require('../images/d.jpg')
    },
    { 
      id: 4, 
      company: 'Databricks', 
      role: 'Recruiter Phone Interview',
      description: 'Advanced to technical interview round',
      image: require('../images/b.jpg')
    },
    { 
      id: 5, 
      company: 'Accenture', 
      role: 'Fraud Consultant',
      description: 'Interview invitation received for consulting position',
      image: require('../images/e.jpg')
    }
  ];

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % successStories.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [successStories.length]);

  // Hide header on scroll down, show on scroll up
  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY;
      if (currentY > lastScrollY + 5) {
        setShowHeader(false);
      } else if (currentY < lastScrollY - 5) {
        setShowHeader(true);
      }
      setLastScrollY(currentY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [lastScrollY]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % successStories.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + successStories.length) % successStories.length);
  };

  // Modal keyboard controls
  useEffect(() => {
    if (!isModalOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setIsModalOpen(false);
      } else if (e.key === 'ArrowRight') {
        const next = (modalIndex + 1) % successStories.length;
        setModalIndex(next);
        setModalImage(successStories[next].image);
      } else if (e.key === 'ArrowLeft') {
        const prev = (modalIndex - 1 + successStories.length) % successStories.length;
        setModalIndex(prev);
        setModalImage(successStories[prev].image);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isModalOpen, modalIndex, successStories]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send email using EmailJS (no backend needed)
      const result = await emailjs.send(
        'service_fg1ubgu',
        'template_03siona',
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          message: `
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}

Message:
${formData.message || 'No message provided'}
          `.trim(),
          to_email: 'bhati.6734@gmail.com'
        },
        '40lFb9qaHJPASYP0d'
      );

      console.log('Email sent successfully:', result);
      
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });
      
      setFormData({ name: '', email: '', phone: '', message: '' });
      
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or contact us directly.",
        variant: "destructive",
      });
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const pageBg = isDark ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900';
  const headerBg = isDark ? 'bg-slate-900/80 border-slate-800' : 'bg-white/80 border-slate-200';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900';
  const sectionBg = isDark ? 'bg-slate-900' : 'bg-white/80';
  const contactBg = isDark ? 'bg-slate-900' : 'bg-slate-100';

  return (
    <div className={`min-h-screen ${pageBg} transition-colors duration-300`}>
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 backdrop-blur border-b ${headerBg} z-50 transition-transform duration-300 ${showHeader ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-600 to-cyan-800 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">UKC</span>
              </div>
              <span className="text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}">UK Work Connect</span>
            </div>
            <nav className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('home')} className={`${isDark ? 'text-slate-200' : 'text-slate-700'} hover:text-teal-400 transition-colors font-medium`}>Home</button>
              <button onClick={() => scrollToSection('about')} className={`${isDark ? 'text-slate-200' : 'text-slate-700'} hover:text-teal-400 transition-colors font-medium`}>About</button>
              <button onClick={() => scrollToSection('services')} className={`${isDark ? 'text-slate-200' : 'text-slate-700'} hover:text-teal-400 transition-colors font-medium`}>Services</button>
              <button onClick={() => scrollToSection('contact')} className={`${isDark ? 'text-slate-200' : 'text-slate-700'} hover:text-teal-400 transition-colors font-medium`}>Contact</button>
            </nav>
            <label className="ml-4 flex items-center space-x-2 cursor-pointer select-none" aria-label="Toggle color theme">
              <div className={`w-12 h-7 rounded-full p-1 transition-colors relative overflow-hidden ${
                isDark ? 'bg-emerald-600' : 'bg-slate-200'
              }`}>
                <Sun
                  className={`absolute left-1 top-1 h-5 w-5 transition-opacity ${
                    isDark ? 'opacity-30 text-amber-200' : 'opacity-100 text-amber-500 drop-shadow'
                  }`}
                />
                <Moon
                  className={`absolute right-1 top-1 h-5 w-5 transition-opacity ${
                    isDark ? 'opacity-100 text-white drop-shadow' : 'opacity-30 text-slate-500'
                  }`}
                />
                <div
                  className={`h-5 w-5 rounded-full bg-white shadow transition-transform relative ${
                    isDark ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </div>
              <input
                type="checkbox"
                checked={isDark}
                onChange={() => setIsDark((prev) => !prev)}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-28 pb-16 bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-800 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center py-10">
            <div className="space-y-6">
              <p className="text-sm uppercase tracking-[0.2em] text-emerald-200">Career consulting partner</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Turning aspirations into achievements
              </h1>
              <p className="text-lg text-emerald-100 max-w-xl">
                Professional Career Guidance Services for Both Experienced and Fresh Talent in the UK.  Let us help you navigate the job market and achieve your career aspirations with confidence.
              </p>
              <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-3 sm:space-y-0">
                <Button 
                  onClick={() => scrollToSection('about')} 
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-8 py-4 text-lg rounded-lg transition-all transform hover:translate-y-[-1px]"
                >
                  Learn More
                </Button>
                <Button 
                  onClick={() => scrollToSection('contact')} 
                  variant="outline"
                  className="border-emerald-300 text-white hover:bg-white/10 px-8 py-4 text-lg rounded-lg"
                >
                  Contact Us
                </Button>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-6 md:p-8 shadow-xl">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-3xl font-bold text-white">20+</p>
                  <p className="text-sm text-emerald-100">Years Experience</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-3xl font-bold text-white">4 Regions</p>
                  <p className="text-sm text-emerald-100">UK · US · Dubai · India</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-3xl font-bold text-white">Staffing</p>
                  <p className="text-sm text-emerald-100">Recruitment & Support</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-3xl font-bold text-white">Profile</p>
                  <p className="text-sm text-emerald-100">Career Acceleration</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section (moved up for clearer narrative) */}
      <section id="about" className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid md:grid-cols-2 gap-12 items-center rounded-2xl shadow-xl p-8 border ${
            isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'
          }`}>
            <div>
              <img 
                src={require('../images/20251207_1838_image.png')} 
                alt="Professional Team" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </div>
            <div className="space-y-4">
              <p className="text-sm uppercase tracking-[0.2em] text-emerald-400">About us</p>
              <h2 className={`text-3xl md:text-4xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Our Mission</h2>
              <p className={`${isDark ? 'text-slate-200' : 'text-slate-600'} leading-relaxed`}>
                Our goal at UK Work Connect Group is to empower job seekers with the right guidance and support to secure opportunities in the UK, US, Dubai, and India. Our team offers comprehensive assistance to help you land your next role and enhance your professional profile. With 20 years of industry expertise, our team efficiently delivers high-quality hiring and staffing support tailored specifically to international job market.
              </p>
              <p className={`${isDark ? 'text-slate-200' : 'text-slate-600'} leading-relaxed`}>
                Leveraging 20 years of industry experience, our professional team excels in recruitment and staffing 
                tailored specifically for the UK job market.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Expert Consulting Services */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm uppercase tracking-[0.2em] text-emerald-700">What we do</p>
          <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Expert Consulting Services</h2>
          <p className={`text-lg max-w-3xl mx-auto mb-10 ${isDark ? 'text-slate-200' : 'text-slate-600'}`}>
            Welcome to the UK Work Connect Group, a reliable partner for specialised consulting services across the UK. We offer customised guidance to advance your career journey and support your success, wherever you are located.
          </p>
          <div className="flex justify-center space-x-6">
            <a href="https://www.instagram.com/ukc_work" target="_blank" rel="noopener noreferrer" 
               className="transition-transform hover:scale-110">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                <rect width="24" height="24" rx="6" fill="url(#instagram-gradient)"/>
                <path d="M12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75ZM12 13.75C11.0332 13.75 10.25 12.9668 10.25 12C10.25 11.0332 11.0332 10.25 12 10.25C12.9668 10.25 13.75 11.0332 13.75 12C13.75 12.9668 12.9668 13.75 12 13.75ZM16.5 7C16.5 7.41421 16.1642 7.75 15.75 7.75C15.3358 7.75 15 7.41421 15 7C15 6.58579 15.3358 6.25 15.75 6.25C16.1642 6.25 16.5 6.58579 16.5 7ZM19.25 12C19.25 10.7574 19.2597 9.52412 19.1955 8.28352C19.1313 6.85608 18.8077 5.58309 17.7537 4.52904C16.6997 3.475 15.4279 3.15131 13.9995 3.08712C12.7574 3.02293 11.5241 3.03271 10.2835 3.03271C9.04289 3.03271 7.80965 3.02293 6.56905 3.08712C5.14061 3.15131 3.86762 3.47499 2.81357 4.52904C1.75952 5.58309 1.43584 6.85608 1.37165 8.28352C1.30746 9.52412 1.31724 10.7574 1.31724 12C1.31724 13.2426 1.30746 14.4759 1.37165 15.7165C1.43584 17.1439 1.75952 18.4169 2.81357 19.471C3.86762 20.525 5.14061 20.8487 6.56905 20.9129C7.80965 20.9771 9.04289 20.9673 10.2835 20.9673C11.5241 20.9673 12.7574 20.9771 13.9995 20.9129C15.4279 20.8487 16.6997 20.525 17.7537 19.471C18.8077 18.4169 19.1313 17.1439 19.1955 15.7165C19.2597 14.4759 19.25 13.2426 19.25 12ZM17.1571 17.2856C16.8909 17.9526 16.4526 18.3909 15.7856 18.6571C14.6367 19.0954 12.0241 18.9996 10.2835 18.9996C8.54289 18.9996 5.93027 19.0954 4.78138 18.6571C4.11436 18.3909 3.67607 17.9526 3.40987 17.2856C2.97155 16.1367 3.06733 13.5241 3.06733 11.7835C3.06733 10.0429 2.97155 7.43027 3.40987 6.28138C3.67607 5.61436 4.11436 5.17607 4.78138 4.90987C5.93027 4.47155 8.54289 4.56733 10.2835 4.56733C12.0241 4.56733 14.6367 4.47155 15.7856 4.90987C16.4526 5.17607 16.8909 5.61436 17.1571 6.28138C17.5954 7.43027 17.4996 10.0429 17.4996 11.7835C17.4996 13.5241 17.5954 16.1367 17.1571 17.2856Z" fill="white"/>
                <defs>
                  <linearGradient id="instagram-gradient" x1="0" y1="24" x2="24" y2="0">
                    <stop stopColor="#FD5949"/>
                    <stop offset="0.5" stopColor="#D6249F"/>
                    <stop offset="1" stopColor="#285AEB"/>
                  </linearGradient>
                </defs>
              </svg>
            </a>
            <a href="https://www.linkedin.com/company/110266900" target="_blank" rel="noopener noreferrer" 
               className="transition-transform hover:scale-110">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                <rect width="24" height="24" rx="4" fill="#0A66C2"/>
                <path d="M7.5 9H5V19H7.5V9ZM6.25 7.75C7.08 7.75 7.75 7.08 7.75 6.25C7.75 5.42 7.08 4.75 6.25 4.75C5.42 4.75 4.75 5.42 4.75 6.25C4.75 7.08 5.42 7.75 6.25 7.75ZM19 19H16.5V14.25C16.5 13.25 16.5 11.92 15.08 11.92C13.66 11.92 13.42 13.08 13.42 14.17V19H10.92V9H13.33V10.13H13.37C13.71 9.49 14.55 8.83 15.78 8.83C18.33 8.83 19 10.5 19 12.75V19Z" fill="white"/>
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className={`py-20 ${sectionBg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm uppercase tracking-[0.2em] text-emerald-700">Services</p>
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Career Consulting Services for Professional and Freshers
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            <Card className={`h-full shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-emerald-200/30 focus-within:-translate-y-2 ${cardBg}`}>
              <CardContent className="p-6 space-y-4">
                <div className="mb-2 rounded-xl overflow-hidden">
                  <img 
                    src={require('../images/20251207_1839_image.png')} 
                    alt="Resume Review" 
                    className="w-full h-48 object-cover"
                  />
                </div>
                <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Resume Review</h3>
                <p className={`${isDark ? 'text-slate-200' : 'text-slate-600'} text-sm`}>
                  A strong CV is essential for gaining access to new chances in the cut throat job market of today.  Our speciality is assisting job searchers like you in creating resumes that not only draw attention but also make an impact.
                </p>
              </CardContent>
            </Card>

            <Card className={`h-full shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-emerald-200/30 focus-within:-translate-y-2 ${cardBg}`}>
              <CardContent className="p-6 space-y-4">
                <div className="mb-2 rounded-xl overflow-hidden">
                  <img 
                    src={require('../images/20251207_1840_image.png')} 
                    alt="Career Guidance" 
                    className="w-full h-48 object-cover"
                  />
                </div>
                <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Career Guidance</h3>
                <p className={`${isDark ? 'text-slate-200' : 'text-slate-600'} text-sm`}>
                  In today’s competitive work market, obtaining new opportunities requires a great resume. Our area of expertise is helping job seekers like you write resumes that stand out and have an impact.
                </p>
              </CardContent>
            </Card>

            <Card className={`h-full shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-emerald-200/30 focus-within:-translate-y-2 ${cardBg}`}>
              <CardContent className="p-6 space-y-4">
                <div className="mb-2 rounded-xl overflow-hidden">
                  <img 
                    src={require('../images/20251207_1841_image.png')} 
                    alt="Interview Support" 
                    className="w-full h-48 object-cover"
                  />
                </div>
                <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Interview Support</h3>
                <p className={`${isDark ? 'text-slate-200' : 'text-slate-600'} text-sm`}>
                  Getting an interview is only the first step. You must do well at the interview in order to get the job you want. We provide thorough interview assistance services such as interview notes that are intended to help you make an impression on prospective employers and confidently present your qualifications.
                </p>
              </CardContent>
            </Card>

            <Card className={`h-full shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-emerald-200/30 focus-within:-translate-y-2 ${cardBg}`}>
              <CardContent className="p-6 space-y-4">
                <div className="mb-2 rounded-xl overflow-hidden">
                  <img 
                    src={require('../images/20251207_1842_image.png')} 
                    alt="Resume Marketing" 
                    className="w-full h-48 object-cover"
                  />
                </div>
                <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Resume Marketing</h3>
                <p className={`${isDark ? 'text-slate-200' : 'text-slate-600'} text-sm`}>
                  Making an outstanding resume is just the beginning of your job quest.  You must present your resume to the appropriate employers if you want to really stand out.  Our speciality is resume marketing services that help you get in front of companies and increase your chances of getting the job of your dreams.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Success Stories Carousel */}
      <section className={`py-20 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] text-emerald-700">Our Track Record</p>
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Client Success Stories
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Real interview invitations from top companies secured by our clients
            </p>
          </div>

          {/* Carousel Container */}
          <div className="relative">
            <div className="overflow-hidden rounded-2xl shadow-2xl">
              <div 
                className="flex transition-transform duration-700 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {successStories.map((story, index) => {
                  const isActive = index === currentSlide;
                  const baseScale = isActive ? 'scale-100' : 'scale-95';
                  return (
                    <div key={story.id} className="min-w-full">
                      <div className={`${isDark ? 'bg-slate-800' : 'bg-white'} p-8 md:p-12 transition-transform duration-500 group ${baseScale} md:hover:scale-100`}>
                        <div className="text-center mb-6">
                          <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {story.company}
                          </h3>
                          <p className={`text-lg ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                            {story.role}
                          </p>
                        </div>
                        <div className="flex justify-center">
                          <div className={`relative w-full max-w-2xl rounded-2xl overflow-hidden border ${
                            isDark ? 'border-emerald-500/30' : 'border-emerald-500/20'
                          } shadow-2xl transition-transform duration-500 ${baseScale} group-hover:scale-100 bg-gradient-to-br from-emerald-500/15 via-cyan-500/8 to-transparent`}> 
                            <div className="absolute inset-0 blur-2xl opacity-25 bg-emerald-400/40 transition-opacity duration-500 group-hover:opacity-55" aria-hidden="true"></div>
                            <button
                              onClick={() => {
                                setModalIndex(index);
                                setModalImage(story.image);
                                setIsModalOpen(true);
                              }}
                              className="relative block w-full focus:outline-none focus:ring-2 focus:ring-emerald-500/60 rounded"
                              aria-label={`Open ${story.company} screenshot`}
                            >
                              <img 
                                src={story.image} 
                                alt={`${story.company} Interview`}
                                className={`relative w-full h-[320px] object-contain bg-white transition-transform duration-500 ${baseScale} group-hover:scale-100`}
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevSlide}
              className={`absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full ${
                isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-white hover:bg-slate-50'
              } shadow-lg transition-all duration-300 hover:scale-110`}
              aria-label="Previous slide"
            >
              <ChevronLeft className={`w-6 h-6 ${isDark ? 'text-white' : 'text-slate-900'}`} />
            </button>
            <button
              onClick={nextSlide}
              className={`absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full ${
                isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-white hover:bg-slate-50'
              } shadow-lg transition-all duration-300 hover:scale-110`}
              aria-label="Next slide"
            >
              <ChevronRight className={`w-6 h-6 ${isDark ? 'text-white' : 'text-slate-900'}`} />
            </button>

            {/* Image Lightbox Modal */}
            {isModalOpen && (
              <div
                className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
                onClick={() => setIsModalOpen(false)}
              >
                <div
                  className="relative max-w-5xl w-[94%] md:w-[86%] lg:w-[76%] p-4 md:p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Close button */}
                  <button
                    className="absolute -top-3 -right-3 md:-top-4 md:-right-4 bg-white/90 text-slate-900 rounded-full shadow p-2 hover:bg-white"
                    onClick={() => setIsModalOpen(false)}
                    aria-label="Close modal"
                  >
                    ✕
                  </button>
                  {/* Prev/Next */}
                  <button
                    className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/90 text-slate-900 rounded-full shadow p-2 hover:bg-white"
                    aria-label="Previous image"
                    onClick={() => {
                      const prev = (modalIndex - 1 + successStories.length) % successStories.length;
                      setModalIndex(prev);
                      setModalImage(successStories[prev].image);
                    }}
                  >
                    ‹
                  </button>
                  <button
                    className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/90 text-slate-900 rounded-full shadow p-2 hover:bg-white"
                    aria-label="Next image"
                    onClick={() => {
                      const next = (modalIndex + 1) % successStories.length;
                      setModalIndex(next);
                      setModalImage(successStories[next].image);
                    }}
                  >
                    ›
                  </button>
                  {/* Image */}
                  <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'}`}>
                    <img
                      src={modalImage}
                      alt="Selected screenshot"
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {successStories.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? 'bg-emerald-600 w-8'
                      : isDark ? 'bg-slate-600 hover:bg-slate-500' : 'bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Success Story Banner */}
      <section className="py-12 bg-gradient-to-r from-emerald-600 to-cyan-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Be the part of our Success Story
          </h2>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className={`py-20 ${contactBg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] text-emerald-700">Get in touch</p>
            <h2 className={`text-3xl md:text-4xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Contact Us</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div className={`${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'} rounded-2xl shadow-xl p-8 border`}>
              <h3 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>Drop us a line!</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input 
                    type="text" 
                    name="name"
                    placeholder="Name*" 
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <Input 
                    type="email" 
                    name="email"
                    placeholder="Email*" 
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <Input 
                    type="tel" 
                    name="phone"
                    placeholder="Phone Number*" 
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <Textarea 
                    name="message"
                    placeholder="Message" 
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full"
                  />
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-6">
                  Send
                </Button>
              </form>
            </div>
            <div className="space-y-6">
              <div className={`${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'} rounded-2xl shadow-xl p-8 border space-y-4`}>
                <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Better yet, Give us a call or drop an email</h3>
                <p className={`${isDark ? 'text-slate-200' : 'text-slate-600'}`}>
                  We value our customers! Please feel free to call us during our normal business hours or send us a message or email.
                </p>
                <div className="space-y-4">
                  <div className={`flex items-center space-x-3 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    <Mail className="w-6 h-6" />
                    <div className="flex flex-col gap-1">
                      <a href="mailto:ukworkconnect@gmail.com" className="hover:text-emerald-600">
                        ukworkconnect@gmail.com
                      </a>
                      <a href="mailto:support@ukworkconnect.co.uk" className="hover:text-emerald-600">
                        support@ukworkconnect.co.uk
                      </a>
                    </div>
                  </div>
                </div>
                <div className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'} p-6 rounded-xl border`}>
                  <h4 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Hours</h4>
                  <div className={`space-y-2 ${isDark ? 'text-slate-200' : 'text-slate-600'}`}>
                    <div className="flex justify-between">
                      <span>Mon - Fri</span>
                      <span>09:00 – 18:00</span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-4 pt-2">
                  <a href="https://www.instagram.com/ukc_work" target="_blank" rel="noopener noreferrer" 
                     className="transition-transform hover:scale-110">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                      <rect width="24" height="24" rx="6" fill="url(#instagram-gradient-footer)"/>
                      <path d="M12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75ZM12 13.75C11.0332 13.75 10.25 12.9668 10.25 12C10.25 11.0332 11.0332 10.25 12 10.25C12.9668 10.25 13.75 11.0332 13.75 12C13.75 12.9668 12.9668 13.75 12 13.75ZM16.5 7C16.5 7.41421 16.1642 7.75 15.75 7.75C15.3358 7.75 15 7.41421 15 7C15 6.58579 15.3358 6.25 15.75 6.25C16.1642 6.25 16.5 6.58579 16.5 7ZM19.25 12C19.25 10.7574 19.2597 9.52412 19.1955 8.28352C19.1313 6.85608 18.8077 5.58309 17.7537 4.52904C16.6997 3.475 15.4279 3.15131 13.9995 3.08712C12.7574 3.02293 11.5241 3.03271 10.2835 3.03271C9.04289 3.03271 7.80965 3.02293 6.56905 3.08712C5.14061 3.15131 3.86762 3.47499 2.81357 4.52904C1.75952 5.58309 1.43584 6.85608 1.37165 8.28352C1.30746 9.52412 1.31724 10.7574 1.31724 12C1.31724 13.2426 1.30746 14.4759 1.37165 15.7165C1.43584 17.1439 1.75952 18.4169 2.81357 19.471C3.86762 20.525 5.14061 20.8487 6.56905 20.9129C7.80965 20.9771 9.04289 20.9673 10.2835 20.9673C11.5241 20.9673 12.7574 20.9771 13.9995 20.9129C15.4279 20.8487 16.6997 20.525 17.7537 19.471C18.8077 18.4169 19.1313 17.1439 19.1955 15.7165C19.2597 14.4759 19.25 13.2426 19.25 12ZM17.1571 17.2856C16.8909 17.9526 16.4526 18.3909 15.7856 18.6571C14.6367 19.0954 12.0241 18.9996 10.2835 18.9996C8.54289 18.9996 5.93027 19.0954 4.78138 18.6571C4.11436 18.3909 3.67607 17.9526 3.40987 17.2856C2.97155 16.1367 3.06733 13.5241 3.06733 11.7835C3.06733 10.0429 2.97155 7.43027 3.40987 6.28138C3.67607 5.61436 4.11436 5.17607 4.78138 4.90987C5.93027 4.47155 8.54289 4.56733 10.2835 4.56733C12.0241 4.56733 14.6367 4.47155 15.7856 4.90987C16.4526 5.17607 16.8909 5.61436 17.1571 6.28138C17.5954 7.43027 17.4996 10.0429 17.4996 11.7835C17.4996 13.5241 17.5954 16.1367 17.1571 17.2856Z" fill="white"/>
                      <defs>
                        <linearGradient id="instagram-gradient-footer" x1="0" y1="24" x2="24" y2="0">
                          <stop stopColor="#FD5949"/>
                          <stop offset="0.5" stopColor="#D6249F"/>
                          <stop offset="1" stopColor="#285AEB"/>
                        </linearGradient>
                      </defs>
                    </svg>
                  </a>
                  <a href="https://www.linkedin.com/company/110266900" target="_blank" rel="noopener noreferrer" 
                     className="transition-transform hover:scale-110">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                      <rect width="24" height="24" rx="4" fill="#0A66C2"/>
                      <path d="M7.5 9H5V19H7.5V9ZM6.25 7.75C7.08 7.75 7.75 7.08 7.75 6.25C7.75 5.42 7.08 4.75 6.25 4.75C5.42 4.75 4.75 5.42 4.75 6.25C4.75 7.08 5.42 7.75 6.25 7.75ZM19 19H16.5V14.25C16.5 13.25 16.5 11.92 15.08 11.92C13.66 11.92 13.42 13.08 13.42 14.17V19H10.92V9H13.33V10.13H13.37C13.71 9.49 14.55 8.83 15.78 8.83C18.33 8.83 19 10.5 19 12.75V19Z" fill="white"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`${isDark ? 'bg-slate-950 text-white' : 'bg-slate-950 text-white'} py-8`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400">Copyright © 2025 UK Work Connect Group - All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;